/**
 * Coach style guard — the Pep/Klopp anti-pattern check (Ben item #5).
 *
 * Founder sync 2026-04-30 + Ben Petty 2026-05-05 note 2: when a grassroots
 * coach picks "possession-based" the AI must not generate a tiki-taka
 * session. The squad's technical floor often can't sustain one-touch
 * combinations under pressure. Coach gets frustrated, kids get frustrated,
 * the session unravels.
 *
 * This module provides the verdict + adjustments. Pure logic separated
 * from the DB-backed entry point so the heuristic is unit-testable.
 *
 * Pairs with coachFocus.ts (theme-variance discipline-imposer). Both
 * surface as planner pre-flight checks.
 */
import type { CoachingStyle } from './coachingStyle';
import type { CoachingTier } from './phaseOfPlay';

export type StyleFitVerdict = 'green' | 'amber' | 'red';

export interface TechniqueFloor {
  /** Mean technical-tactical rating (0–100) across recent active players,
   *  or null when no rating data is available yet. */
  meanTechRating: number | null;
  /** How many players' ratings informed the floor. */
  playerCount: number;
  /** Lookback window in days. Default 90 — long enough to span a season
   *  cycle, short enough that very old ratings don't dominate. */
  lookbackDays: number;
}

export interface StyleFitInput {
  style: CoachingStyle;
  tier: CoachingTier;
  /** Mean technical-tactical rating (0–100), or null when unknown. */
  meanTechRating: number | null;
}

/**
 * Pure heuristic: returns the technique-fit verdict for the given style.
 *
 * Thresholds are first-pass and deliberately conservative. They lean
 * toward "amber" when in doubt — the goal is to surface the choice for
 * the coach, not to block. Refine when we have rating telemetry.
 *
 * Logic:
 *   - direct_play       → always green (lowest technique demand)
 *   - counter_attacking → green at academy; amber if grassroots and tech < 50
 *   - possession_based  → grassroots is the high-risk case:
 *                            tech >= 70 → green
 *                            tech 55-69 → amber  (possession through thirds OK, one-touch is a stretch)
 *                            tech < 55  → red    (one-touch under pressure will fail)
 *                            tech null  → amber  (unknown, default cautious)
 *                          academy assumed capable → green
 */
export function evaluateStyleFit(input: StyleFitInput): StyleFitVerdict {
  const { style, tier, meanTechRating } = input;

  if (style === 'direct_play') return 'green';

  if (style === 'counter_attacking') {
    if (tier === 'academy') return 'green';
    if (meanTechRating == null) return 'green';
    if (meanTechRating < 50) return 'amber';
    return 'green';
  }

  if (style === 'possession_based') {
    if (tier === 'academy') return 'green';
    if (meanTechRating == null) return 'amber';
    if (meanTechRating < 55) return 'red';
    if (meanTechRating < 70) return 'amber';
    return 'green';
  }

  return 'green';
}

/** Concrete adjustment suggestions. The planner UI surfaces these as
 *  swap-in options when the verdict is amber/red. The AI message picks
 *  ONE to weave into the warning sentence; the rest appear under it as
 *  one-tap chips ("apply two-touch rule", etc.). */
export function suggestStyleAdjustments(
  style: CoachingStyle,
  verdict: StyleFitVerdict,
): string[] {
  if (verdict === 'green') return [];

  if (style === 'possession_based') {
    if (verdict === 'red') {
      return [
        'Two-touch minimum — ban one-touch passing for now',
        'Build a 4-second back-pass rule, not a continuous-combinations rondo',
        'Wider grids — 25×35 yards instead of 15×20',
        'Simple support play first — pass + offer the second option, not first-time combinations',
      ];
    }
    return [
      'Two-touch as the default; one-touch as a progression late in the session',
      'Patterns through the thirds rather than continuous one-touch',
    ];
  }

  if (style === 'counter_attacking') {
    return [
      'Slow the build down — five-pass minimum before the break',
      'Receive-and-look first; the break is option B, not option A',
    ];
  }

  return [];
}

export interface CoachStyleReport {
  style: CoachingStyle;
  tier: CoachingTier;
  floor: TechniqueFloor;
  verdict: StyleFitVerdict;
  /** True when the planner should surface the message before generation. */
  shouldWarn: boolean;
  /** AI-drafted message in coach-mentor voice. Null when shouldWarn is
   *  false OR when AI drafting was skipped / failed. */
  message: string | null;
  /** Concrete adjustments to swap in. Empty when verdict is green. */
  adjustments: string[];
}
